import { useState, useEffect, useCallback } from "react";
import { Menu, X, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { Link, useNavigate, useLocation } from "react-router-dom";
import PetrocontrolLogo from "@/components/PetrocontrolLogo";

export default function Header({ onBookingOpen }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useLanguage();
  const settings = useSiteSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  const NAV_LINKS = [
    { label: "Problemática", href: "#problematica" },
    { label: t("nav.services"), href: "#practice-areas" },
    { label: t("nav.about"), href: "#about" },
    { label: t("nav.contact"), href: "#contact" },
  ];

  const handleScroll = useCallback(() => setScrolled(window.scrollY > 20), [setScrolled]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const scrollTo = (href) => {
    setMobileOpen(false);
    if (!isHome) {
      navigate("/" + href);
      return;
    }
    if (href === "#hero") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="main-header"
      role="banner"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !isHome
          ? "bg-[#F5F6F3]/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="header-logo"
            className="flex items-center gap-2.5 cursor-pointer"
          >
            <PetrocontrolLogo className="h-12 md:h-14" />
          </Link>

          {/* Center Nav */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => scrollTo(link.href)}
                className="text-base text-[#152238]/70 hover:text-[#152238] transition-colors duration-200 font-medium"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Right: Client Access + CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <Link
              to="/portal"
              data-testid="client-portal-link"
              className="flex items-center gap-2 text-sm text-[#152238]/70 hover:text-[#103F38] transition-colors font-medium"
            >
              <LogIn className="h-4 w-4" />
              {t("nav.clientAccess")}
            </Link>
            <Button
              data-testid="header-cta-button"
              onClick={onBookingOpen}
              className="bg-[#103F38] text-white hover:bg-[#0c332d] rounded-full px-7 py-5 text-sm font-medium transition-all duration-300"
            >
              {t("headerCta")}
            </Button>
          </div>

          {/* Mobile Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden text-[#152238] p-2"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#F5F6F3] border-t border-[#e2e4e0] pb-6">
          <nav className="flex flex-col px-6 pt-4 gap-4">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollTo(link.href)}
                className="text-left text-[#152238]/70 hover:text-[#152238] transition-colors py-2 font-medium"
              >
                {link.label}
              </button>
            ))}
            <Link
              to="/portal"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 text-[#103F38] py-2 font-medium"
            >
              <LogIn className="h-4 w-4" />
              {t("nav.clientAccess")}
            </Link>
            <Button
              onClick={() => { setMobileOpen(false); onBookingOpen(); }}
              className="bg-[#103F38] text-white hover:bg-[#0c332d] rounded-full px-6 py-5 font-medium w-full"
            >
              {t("headerCta")}
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
